import type { FieldValidation } from '@rjsf/utils';

/**
 * Sentinel written by GitHubOAuthField once a token has been proven against
 * every repo/org the run touches. Any other value means "not proven yet".
 *
 * A sentinel rather than the token itself: the field value lands in the task's
 * `values` and is persisted and logged, so the token must never go there — it
 * belongs in `secrets`, which is where the field puts it.
 */
export const GITHUB_ACCESS_OK = 'authorized';

/**
 * Sentinel written while the access probe is still in flight.
 *
 * Distinct from the empty string on purpose. Both block the step, but they are
 * different situations and the user can only act on one of them: "not
 * authorized" is something to fix, "still checking" is something to wait for.
 * Writing the same empty string for both is what produced the form's worst
 * message — telling someone to resolve an access problem while the green
 * "access authorized" line sat directly above it, because the probe had landed
 * between the click and the render.
 *
 * That race is easy to lose on a prefilled draft: every field arrives filled
 * in, so the last step is reachable in about a second, while the probe is four
 * GitHub round trips. Filling the form by hand takes long enough to hide it.
 *
 * Never reaches a task: validation rejects it, exactly as it rejects "".
 */
export const GITHUB_ACCESS_PENDING = '__github-access-pending__';

const PENDING_MESSAGE =
  'Still checking your GitHub access — this takes a moment. Try again once the ' +
  'status below settles.';

/**
 * Blocks a scaffolder step until GitHub access has been proven.
 *
 * Needed because neither of the obvious mechanisms actually gates:
 *
 * - JSON Schema `required` only asserts that the property is present, so the
 *   empty string these fields write when access is missing satisfies it. AJV
 *   reports no error for `{ repoUrl: "" }` against `required: ["repoUrl"]`.
 * - A field extension's `validation` is only invoked for keys that exist in
 *   formData (the scaffolder's createAsyncValidators iterates
 *   `Object.entries(formData)`), so a field that writes nothing at all is
 *   never validated.
 *
 * So the fields always write a value, and this rejects the ones that mean
 * "not connected". Without it the form shows a red access status and still
 * lets the user press Create — the run then proceeds without
 * USER_OAUTH_TOKEN, falls back to the backend's own GitHub identity, and
 * either acts under the wrong account or dies mid-run with the bare 404 that
 * SAML SSO returns for an unauthorized token.
 */
function validateGithubAccess(
  value: string | undefined,
  field: FieldValidation,
  message: string,
): void {
  if (value === GITHUB_ACCESS_PENDING) {
    field.addError(PENDING_MESSAGE);
    return;
  }
  if (!value) {
    field.addError(message);
  }
}

/** `repoUrl`-style fields: empty until the probe succeeds. */
export function validateRepoUrlAccess(
  value: string | undefined,
  field: FieldValidation,
): void {
  validateGithubAccess(
    value,
    field,
    'Connect GitHub and resolve the access problem shown above before continuing. ' +
      'Backstage creates the repository and opens the pull requests as you, so it needs your access first.',
  );
}

/** `_githubToken`-style fields: the sentinel above, or empty. */
export function validateGithubTokenField(
  value: string | undefined,
  field: FieldValidation,
): void {
  if (value === GITHUB_ACCESS_PENDING) {
    field.addError(PENDING_MESSAGE);
    return;
  }
  if (value !== GITHUB_ACCESS_OK) {
    field.addError(
      'Connect GitHub and resolve the access problem shown above before continuing. ' +
        'The run opens pull requests on both repositories as you, so your access has to be proven first.',
    );
  }
}
